<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Inventaire</h1>
          <div v-if="inventory">
            <small class="text-muted">
              Référence : <strong>{{ inventory.reference }}</strong> ·
              Type : <strong>{{ inventory.type === 'global' ? 'Global' : 'Partiel' }}</strong> ·
              Statut : <strong>{{ statusLabel(inventory.status) }}</strong> ·
              Date : {{ $format.date(inventory.created_at) }}
            </small>
          </div>
        </div>

        <div class="d-flex gap-2">
          <button
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste"
            @click="goBack"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>
          <button
            v-if="canCountInventory"
            type="button"
            class="btn btn-primary btn-sm btn-icon-label"
            title="Compter l’inventaire"
            aria-label="Compter l’inventaire"
            :disabled="isProcessingAction"
            @click="openInventoryCount"
          >
            <i class="fa fa-list-check"></i>
            <span class="btn-label ms-1">Compter</span>
          </button>
          <button
            v-if="canCloseInventory"
            type="button"
            class="btn btn-outline-warning btn-sm btn-icon-label"
            title="Clôturer le brouillon"
            :disabled="isProcessingAction"
            @click="closeInventory"
          >
            <i class="fa fa-lock"></i>
            <span class="btn-label ms-1">Clôturer</span>
          </button>
          <button
            v-if="canValidateInventory"
            type="button"
            class="btn btn-outline-success btn-sm btn-icon-label"
            title="Valider le stock"
            :disabled="isProcessingAction"
            @click="validateInventory"
          >
            <i class="fa fa-check"></i>
            <span class="btn-label ms-1">Valider le stock</span>
          </button>
          <AppLoadingButton
            v-if="canDeleteInventory"
            type="button"
            class="btn btn-outline-danger btn-sm btn-icon-label"
            title="Supprimer le brouillon"
            :loading="isProcessingAction"
            loading-label="Suppression..."
            @click="deleteInventory"
          >
            <i class="fa fa-trash"></i>
            <span class="btn-label ms-1">Supprimer</span>
          </AppLoadingButton>
        </div>
      </div>

      <div v-if="isLoading" class="alert alert-info py-2">
        Chargement de l’inventaire...
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>

      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <div v-if="inventory">
        <!-- RAPPORT / STATS -->
        <div class="row mb-3">
          <div class="col-md-3">
            <div class="card border-0 shadow-sm">
              <div class="card-body py-2">
                <div class="small text-muted">Produits scannés</div>
                <div class="h5 mb-0">{{ countedLines }} / {{ totalLines }}</div>
              </div>
            </div>
          </div>

          <div class="col-md-3">
            <div class="card border-0 shadow-sm">
              <div class="card-body py-2">
                <div class="small text-muted">Anomalies</div>
                <div class="h5 mb-0">{{ anomaliesCount }}</div>
                <small class="text-muted">
                  {{ overstockCount }} surstock · {{ understockCount }} sous-stock
                </small>
              </div>
            </div>
          </div>

          <div class="col-md-3">
            <div class="card border-0 shadow-sm">
              <div class="card-body py-2">
                <div class="small text-muted">Total écart (Qté)</div>
                <div class="h5 mb-0">{{ totalDiff }}</div>
              </div>
            </div>
          </div>

          <div class="col-md-3">
            <div class="card border-0 shadow-sm">
              <div class="card-body py-2">
                <div class="small text-muted">Produits sans écart</div>
                <div class="h5 mb-0">{{ countedLines - anomaliesCount }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Détails des produits</h5>
          <small v-if="inventoryItems.length" class="text-muted">
            {{ inventoryItems.length }} produit(s)
          </small>
        </div>

        <BootstrapDataTable
          :rows="inventoryItems"
          :columns="inventoryItemColumns"
          row-key="id"
          :per-page="10"
          export-file-name="details-produits-inventaire.xlsx"
          print-title="Détails des produits de l’inventaire"
          empty-text="Aucun produit dans cet inventaire."
          @refresh="fetchInventory"
        >
          <template #cell-product_name="{ row }">
            <span class="fw-semibold">{{ row.product_name || '-' }}</span>
          </template>

          <template #cell-rayon="{ row }">
            <span class="rayon-badge" :style="{ borderColor: rayonColor(row.rayon_color) }">
              <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(row.rayon_color) }"></span>
              <span>{{ row.rayon || '-' }}</span>
            </span>
          </template>

          <template #cell-stock_quantity="{ row }">
            <span class="text-end d-block">{{ row.stock_quantity ?? 0 }}</span>
          </template>

          <template #cell-real_quantity="{ row }">
            <span v-if="row.real_quantity === null" class="badge bg-light text-muted border">
              Non compté
            </span>
            <span v-else class="text-end d-block">{{ row.real_quantity }}</span>
          </template>

          <template #cell-difference="{ row }">
            <span
              v-if="row.difference !== null"
              :class="[
                'fw-semibold',
                row.difference > 0
                  ? 'text-success'
                  : row.difference < 0
                    ? 'text-danger'
                    : ''
              ]"
            >
              {{ row.difference }}
            </span>
            <span v-else>-</span>
          </template>
        </BootstrapDataTable>
      </div>
    </template>
  </AdminLayout>
</template>

<script>
import { normalizeRayonColor } from '@/utils/rayonColor';

export default {
  name: 'InventoryShowView',
  data() {
    return {
      inventory: null,
      isLoading: false,
      isProcessingAction: false,
      errorMessage: '',
      successMessage: '',
      inventoryItemColumns: [
        { key: 'product_name', label: 'Produit' },
        { key: 'rayon', label: 'Rayon' },
        { key: 'stock_quantity', label: 'Qté théorique', class: 'text-end' },
        { key: 'real_quantity', label: 'Qté réelle', class: 'text-end' },
        { key: 'difference', label: 'Écart', class: 'text-end' },
      ],
    };
  },
  computed: {
    inventoryItems() {
      return this.inventory?.items || [];
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'] || {};
    },
    isGerant() {
      return this.currentUser.role === 'gerant';
    },
    canCloseInventory() {
      return this.isGerant && this.inventory?.status === 'draft';
    },
    canCountInventory() {
      return this.isGerant && this.inventory?.status === 'draft';
    },
    canValidateInventory() {
      return this.isGerant && this.inventory?.status === 'closed';
    },
    canDeleteInventory() {
      return this.isGerant && this.inventory?.status === 'draft';
    },
    totalLines() {
      return this.inventory ? this.inventory.items.length : 0;
    },
    countedLines() {
      if (!this.inventory) return 0;
      return this.inventory.items.filter((it) => it.real_quantity !== null).length;
    },
    anomaliesCount() {
      if (!this.inventory) return 0;
      return this.inventory.items.filter((it) => it.difference !== null && it.difference !== 0).length;
    },
    overstockCount() {
      if (!this.inventory) return 0;
      return this.inventory.items.filter((it) => it.difference > 0).length;
    },
    understockCount() {
      if (!this.inventory) return 0;
      return this.inventory.items.filter((it) => it.difference < 0).length;
    },
    totalDiff() {
      if (!this.inventory) return 0;
      return this.inventory.items.reduce((sum, it) => sum + Number(it.difference || 0), 0);
    },
  },
  mounted() {
    this.fetchInventory();
  },
  methods: {
    rayonColor(color) {
      return normalizeRayonColor(color);
    },

    async fetchInventory() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      const id = this.$route.params.id;

      try {
        const response = await this.$api.get(`/inventories/${id}`);
        this.inventory = response.data.data;
      } catch (error) {
        this.errorMessage = "Impossible de charger l’inventaire.";
        this.inventory = null;
      } finally {
        this.isLoading = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'Inventories' });
    },

    openInventoryCount() {
      if (!this.canCountInventory) return;

      this.$router.push({
        name: 'InventoryCount',
        params: { id: this.inventory.id },
      });
    },

    statusLabel(status) {
      switch (status) {
        case 'draft':
          return 'Brouillon';
        case 'closed':
          return 'Clos';
        case 'validated':
          return 'Validé';
        default:
          return status || '-';
      }
    },

    async closeInventory() {
      if (!this.canCloseInventory) return;

      const ok = await this.$confirmAction(
        `Clôturer l’inventaire "${this.inventory.reference}" ? Le comptage sera bloqué sans appliquer le stock.`
      );
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isProcessingAction = true;

      try {
        await this.$api.post(`/inventories/${this.inventory.id}/close`);
        await this.fetchInventory();
        this.successMessage = 'Inventaire clôturé.';
      } catch (error) {
        this.errorMessage = this.errorMessageFrom(error, 'Une erreur est survenue lors de la clôture de l’inventaire.');
      } finally {
        this.isProcessingAction = false;
      }
    },

    async validateInventory() {
      if (!this.canValidateInventory) return;

      const ok = await this.$confirmAction(
        `Valider l’inventaire "${this.inventory.reference}" ? Le stock des produits sera mis à jour.`
      );
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isProcessingAction = true;

      try {
        await this.$api.post(`/inventories/${this.inventory.id}/validate`);
        await this.fetchInventory();
        this.successMessage = 'Inventaire validé avec succès.';
      } catch (error) {
        this.errorMessage = this.errorMessageFrom(error, "Une erreur est survenue lors de la validation de l’inventaire.");
      } finally {
        this.isProcessingAction = false;
      }
    },

    async deleteInventory() {
      if (!this.canDeleteInventory) return;
      if (this.isProcessingAction) return;

      const ok = await this.$confirmAction(
        `Voulez-vous vraiment supprimer l’inventaire "${this.inventory.reference}" ?`
      );
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isProcessingAction = true;

      try {
        await this.$api.delete(`/inventories/${this.inventory.id}`);
        this.$router.push({ name: 'Inventories' });
      } catch (error) {
        this.errorMessage = this.errorMessageFrom(error, "Une erreur est survenue lors de la suppression de l’inventaire.");
      } finally {
        this.isProcessingAction = false;
      }
    },

    errorMessageFrom(error, fallback) {
      return error.response && error.response.data && error.response.data.errors
        ? Object.values(error.response.data.errors).flat().join(' ')
        : fallback;
    },
  },
};
</script>

<style scoped>
.rayon-badge {
  align-items: center;
  background: #ffffff;
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  gap: 0.4rem;
  padding: 0.12rem 0.55rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.75rem;
  width: 0.75rem;
}
</style>
